/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Pagination, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/pagination";

import Tagline from "../../../components/tagline";
import Navbar from "../../../components/navbar";
import ProductDetail from "../../../components/product-detail";
import ArrivalItem from "../../../components/arrival-item";
import Footer from "../../../components/footer";
import ScrollToTop from "../../../components/scroll-to-top";
import ImageModal from "../../../components/ImageModal";
import axiosInstance from "../../../utils/axiosInstance";
import { handleGetLanguage } from "../../../utils/languageStorage";
import Loading from "../../../components/loading";
import { Helmet } from "react-helmet";
import useUserData from "../../../store/userStore";
import useProductData from "../../../store/productStore";
import useCartStore from "../../../store/cartStore";

import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

const fetchData = async (id) => {
  const res = await axiosInstance.get(`/products/${id}`);
  if (res.status !== 200) throw new Error("Something went wrong");
  return res.data;
};

const fallbackImg = "https://placehold.co/600x600?text=No+Image";

export default function ProductDetailThree() {
  const language = handleGetLanguage();
  const { user } = useUserData((s) => s);
  const { setProductData } = useProductData((s) => s);
  const addToCart = useCartStore((s) => s.addToCart);

  const { pathname } = useLocation();
  const referralLink = `https://onlybrands.ge${pathname}?referral_code=${user.referral_code}`;
  const prefix = "https://api.onlybrands.ge/storage/";

  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImg, setModalImg] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const { id } = useParams();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["singleProduct", id],
    queryFn: () => fetchData(id),
  });

  const product = data?.product;

  useEffect(() => {
    if (product) {
      if (!selectedSize && product?.sizes?.length > 0) {
        const defaultSize = product.sizes.find(sz => sz.id === 1);
        if (defaultSize) setSelectedSize(defaultSize);
      }

      if (!selectedColor && product?.colors?.length > 0) {
        const defaultColor = product.colors.find(clr => clr.id === 1);
        if (defaultColor) setSelectedColor(defaultColor);
      }

      setProductData({
        ...product,
        referralLink,
        selectedSize,
        selectedColor,
      });
    }
  }, [product]);

  if (isLoading) return <Loading />;
  if (error) navigate("/error");

  const images = product?.images?.length > 0 ? product.images : [fallbackImg];
  const hasSizes = product?.sizes?.length > 0;
  const hasColors = product?.colors?.length > 0;

  const t = {
    en: { Onlybrands: "Onlybrands" },
    ka: { Onlybrands: "მხოლოდ ბრენდები" },
  }[language] || { Onlybrands: "Onlybrands" };

  const handleAddToCart = () => {
    const fallbackSize = product?.sizes?.find(sz => sz.id === 1) || null;
    const fallbackColor = product?.colors?.find(clr => clr.id === 1) || null;

    const finalSize = selectedSize || fallbackSize;
    const finalColor = selectedColor || fallbackColor;

    if (hasSizes && !finalSize) {
      setErrMsg("ზომა ვერ მოიძებნა / Default size not found");
      return;
    }
    if (hasColors && !finalColor) {
      setErrMsg("ფერი ვერ მოიძებნა / Default color not found");
      return;
    }

    setErrMsg("");

    addToCart({
      product,
      size: finalSize,
      size_id: finalSize?.id || null,
      color: finalColor,
      color_id: finalColor?.id || null,
      quantity: 1,
    });
  };

  return (
    <>
      <Helmet>
        <title>{product.name[language]} | OnlyBrands</title>
        <meta property="og:title" content={product.name[language]} />
        <meta property="og:description" content="100%-ით ორიგინალი" />
        <meta property="og:image" content={`${prefix}${product.images[0]}`} />
        <meta property="og:url" content={referralLink} />
        <link rel="canonical" href={referralLink} />
      </Helmet>

      {modalImg && (
        <ImageModal
          images={images}
          imageUrl={modalImg}
          onClose={() => setModalImg(null)}
        />
      )}

      <Tagline />
      <Navbar navClass="defaultscroll is-sticky tagline-height" />

      <section className="relative table w-full py-20 lg:py-24 md:pt-28 bg-gray-50 dark:bg-slate-800">
        <div className="max-w-screen-xl mx-auto px-4 relative">
          <h3 className="mt-14 text-3xl font-semibold">
            {product.name[language]}
          </h3>
          <ul className="mt-3 tracking-[0.5px] inline-block">
            <li className="inline-block uppercase text-[13px] font-bold hover:text-orange-500 duration-200">
              <Link to="/">{t.Onlybrands}</Link>
            </li>
            <li className="inline-block mx-1 text-slate-500">/</li>
            <li className="inline-block uppercase text-[13px] font-bold hover:text-orange-500 duration-200">
              <Link to={`/shop?categories=${product?.categories[0]?.id}`}>
                {product?.categories[0]?.name?.[language]}
              </Link>
            </li>
            <li className="inline-block mx-1 text-slate-500">/</li>
            <li className="inline-block uppercase text-[13px] font-bold hover:text-orange-500 duration-200">
              <Link to={`/shop?brands=${product?.brand?.id}`}>
                {product?.brand?.name?.[language]}
              </Link>
            </li>
            <li className="inline-block mx-1 text-slate-500">/</li>
            <li className="inline-block uppercase text-[13px] font-bold text-orange-500">
              {product?.name?.[language]}
            </li>
          </ul>
        </div>
      </section>

      <section className="overflow-x-hidden sm:mt-0 -mt-[120px]">
        <div className="max-w-screen-xl mx-auto lg:px-0 px-4 relative py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row gap-10 lg:items-start">
            <div className="flex flex-col gap-6 w-full lg:flex-1">
              <div className="lg:grid grid-cols-[90px_1fr] gap-4">
                <Swiper
                  onSwiper={setThumbsSwiper}
                  modules={[Thumbs, FreeMode]}
                  spaceBetween={10}
                  watchSlidesProgress
                  freeMode
                  direction="horizontal"
                  slidesPerView="auto"
                  className="lg:hidden mb-4"
                >
                  {images.map((img, i) => (
                    <SwiperSlide
                      key={i}
                      className={`w-24 max-h-24 cursor-pointer !h-24 ${i === currentImageIndex ? "border-2 border-orange-500" : "border"}`}
                      onClick={() => mainSwiper?.slideToLoop(i)}
                    >
                      <img
                        src={img.includes("http") ? img : prefix + img}
                        alt={`thumb-${i}`}
                        className="w-full h-full object-cover rounded hover:opacity-80"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                <Swiper
                  onSwiper={setThumbsSwiper}
                  modules={[Thumbs, FreeMode]}
                  spaceBetween={10}
                  watchSlidesProgress
                  slidesPerView={images.length}
                  direction="vertical"
                  className="hidden lg:block"
                  style={{ maxHeight: "600px" }}
                >
                  {images.map((img, i) => (
                    <SwiperSlide
                      key={i}
                      className={`!h-24 cursor-pointer ${i === currentImageIndex ? "border-2 border-orange-500" : "border"}`}
                      onClick={() => mainSwiper?.slideToLoop(i)}
                    >
                      <img
                        src={img.includes("http") ? img : prefix + img}
                        alt={`thumb-${i}`}
                        className="w-full h-full object-cover rounded hover:opacity-80"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

 <Swiper
  modules={[Navigation, Thumbs, Pagination, FreeMode]}
  navigation
  pagination={{ clickable: true }}
  loop
  touchStartPreventDefault={false}
  touchMoveStopPropagation={false}
  style={{ '--swiper-navigation-color': '#f97316' }}
  onSwiper={setMainSwiper}
  onSlideChange={(swiper) => setCurrentImageIndex(swiper.realIndex)}
  thumbs={{
    swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
  }}
  className="w-full h-80 sm:h-96 md:h-[500px] lg:h-[600px] rounded-lg overflow-hidden bg-white flex-1"
>
  {images.map((img, i) => (
    <SwiperSlide key={i}>
      <div className="w-full h-full bg-white flex items-center justify-center touch-pan-y">
        <img
          src={img.includes("http") ? img : prefix + img}
          alt={`product-${i}`}
          className="max-w-full max-h-full object-contain cursor-pointer"
          style={{ touchAction: "pan-y" }}
          onClick={() => setModalImg(img)}
        />
      </div>
    </SwiperSlide>
  ))}
</Swiper>


              </div>
            </div>

            <div className="w-full lg:w-1/3">
              <ProductDetail
                product={product}
                selectedSize={selectedSize}
                setSelectedSize={setSelectedSize}
              />

              {hasSizes && (
                <div className="mt-6">
                  <h4 className="font-semibold mb-2">აირჩიე ზომა:</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((sz) => (
                      <button
                        key={sz.id}
                        onClick={() => setSelectedSize(sz)}
                        className={`px-3 py-1 border rounded-md ${selectedSize?.id === sz.id
                          ? "bg-orange-500 text-white"
                          : "bg-white text-black border-gray-300"
                          } hover:scale-105 transition`}
                      >
                        {sz.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <ArrivalItem data={data?.newArrivals} language={language} />
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </>
  );
}
